<template>
  <div v-if="job" class="profile-list-item list-group-item media">
    <div class="row">
      <div :class="{ 'col-xs-2': !isCompact(), 'col-xs-1': isCompact() }">
        <VLazyImage :src="imgSrc" class="img-circle" />
      </div>

      <div
        :class="{
          'col-xs-10 col-sm-7': !isCompact(),
          'col-xs-11 col-sm-9': isCompact(),
        }"
      >
        <h4 class="m-t-0 m-b-5">
          <span
            v-if="job.complianceNotice"
            v-tooltip="job.complianceNotice.tooltip"
            class="faded complianceNoticeLogo"
          >
            <VLazyImage :src="job.complianceNotice.logoPath" />
          </span>
          <a :href="'/job/' + job.id" class="m-r-5">
            {{ job.name }}
          </a>
          <span
            v-if="job.association"
            class="label f-13 p-t-0 p-b-0 m-l-0 f-400 label-default bgm-lightgray c-gray"
          >
            {{ $t(job.association + '.text') }}
          </span>
        </h4>
        <span v-if="job.company && job.company.id && !anonymized">
          {{ job.company.company || $t('Company') }}
        </span>
        <span v-else-if="!anonymized">
          {{ job.company && job.company.company ? job.company.company : $t('Company') }}
        </span>
        <p v-else style="margin: 0; display: inline">
          {{ $t('Company') }}
        </p>

        {{
          //Renders the region of the job
          locationString()
        }}
        <span
          v-if="
            (filter.markets.selected.length !== 1 &&
              filter.markets.list &&
              Object.keys(filter.markets.list).length > 1 &&
              job.market) ||
            (recommended && job.market)
          "
          :class="{ pointer: recommended }"
          class="label circle circle-10 label-basic"
          @click="addMarketToSearch"
        >
          {{ job.market.toUpperCase() }}
        </span>
      </div>

      <div v-if="!isCompact()" class="col-sm-1 col-xs-2 text-center">
        <span v-if="!anonymized && canLike && job.id" class="heart">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <profile-like :liked="job.liked" :card-type="jobEnum" :card-id="job.id.toString()" />
        </span>
      </div>
      <div class="m-b-10 col-xs-12 visible-xs" />
      <div class="col-sm-2 col-xs-12 text-right">
        <div v-if="!isCompact()">
          <span class="lead f-18">
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <CurrencyDisplay
              :show-converted-rates-by-default="
                Boolean($store.state.user.automatic_currency_conversion)
              "
              :currency-code="job.currency ? job.currency : account.currency"
              :converted-currency-code="account.currency ? account.currency : 'Undefined'"
              :rate="Number(job.rate)"
              :range-from="Number(job.rate_from)"
              :range-to="Number(job.rate_to)"
              :show-currency-code="true"
              :ceiling="true"
            />
          </span>
          <div class="hidden-xs" />
          <small class="text-muted">
            {{ $t('rate.' + job.ratetype + '_slash') }}
          </small>
          <div class="hidden-xs m-b-5" />
        </div>
        <a
          :class="{ 'btn-xs': job.dialog && job.dialogs_count > 1 }"
          :href="'/job/' + job.id + (job.dialog ? '/dialog' : '')"
          class="btn btn-default pull-right-xs pull-right-sm pull-right-md waves-effect"
        >
          {{
            $t(job.dialog ? (job.dialogs_count > 1 ? 'conversations' : 'conversation') : 'View job')
          }}
        </a>
      </div>
    </div>
    <div class="m-b-10 col-xs-12 visible-xs" />
    <div class="row">
      <div
        :class="{
          'col-sm-offset-2 col-sm-8': !isCompact(),
          'col-sm-offset-1 col-sm-9': isCompact(),
        }"
        class="col-xs-12 m-t-5"
      >
        <div v-if="job.skills" class="skills">
          <a
            v-for="(skill, i) in job.skills.slice(0, skillsShown - 1)"
            :key="i"
            :class="{
              label: true,
              'label-default': isCompact() ? false : i <= 2,
              'bgm-purple': isCompact() ? false : i <= 2,
              'label-basic': isCompact() ? true : i > 2,
            }"
            v-on="!isCompact() ? { click: () => addSkillToSearch(skill) } : {}"
          >
            {{ skill.name }}
          </a>
          <a
            v-if="job.skills.length > 5 && skillsShown <= 5 && !isCompact()"
            href="#"
            class="label label-default"
            @click="showAllSkills(job.skills.length)"
          >
            +
            {{ $t(':count more', { count: job.skills.length - skillsShown }) }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import VLazyImage from 'v-lazy-image'
</script>

<script>
import { jobs } from '@/api/vue-api'
import { FILTER_MUTATIONS } from '@/state/mutation-types'
import enums from '@/utils/enum'

export default {
  name: 'DisplayJob',
  props: {
    refJob: {
      type: Object,
      required: true,
    },
    recommended: {
      type: Boolean,
      required: false,
      default: false,
    },
    version: {
      type: String,
      required: false,
    },
    format: {
      type: String,
      required: false,
    },
    canLike: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
  },
  data() {
    return {
      job: null,
      skillsShown: 5, // how many skills shown by default
      imageSrc: '/gfx/missing-logo.png',
      updateingLike: false,
    }
  },
  computed: {
    anonymized() {
      return (
        !this.account.isApproved ||
        (this.account.isCompany && !this.account.isTrusted && !this.account.isVerified) ||
        this.account.isGuest ||
        this.user.isGuest
      )
    },
    jobEnum() {
      return enums.cards.job
    },
    filter() {
      return this.$store.getters.filter
    },
    imgSrc() {
      return this.anonymized
        ? '/gfx/missing-logo.png'
        : this.imageSrc ?? '/gfx/missing-logo.png?base'
    },
  },
  created() {
    this.job = this.refJob
    this.imageSrc =
      this.job.company && this.job.company.avatar_path
        ? this.job.company.avatar_path
        : '/gfx/missing-logo.png'
  },
  methods: {
    addSkillToSearch(skill) {
      if (!this.filter.search.term.includes(skill.name)) {
        this.$store.commit(
          FILTER_MUTATIONS.SET_SEARCH_TERM,
          `${this.$store.state.filter.search.term},${skill.name}`,
        )
      }
    },
    addMarketToSearch() {
      if (!this.filter.markets.selected.includes(this.job.market)) {
        const selected = this.filter.markets.selected.concat([this.job.market])
        this.$store.commit(FILTER_MUTATIONS.MARKET_SELECTED, selected)
      }
    },
    showAllSkills(numSkills) {
      this.skillsShown = numSkills
    },
    async toggleLike() {
      if (this.updateingLike) return
      this.job.liked = !this.job.liked
      this.updateingLike = true
      try {
        await jobs.likeJob(this.job.id, this.job.liked)
        this.updateingLike = false
      } catch (e) {
        this.updateingLike = false
        this.$store.commit('sendNotification', {
          type: enums.notification.types.ERROR,
          msg: this.$t(
            `An error occurred when trying to ${this.job.liked ? 'like' : 'unlike'} the job`,
          ),
          sender: this,
          severity: enums.notification.severity.CRITICAL,
        })
        this.job.liked = !this.job.liked
      }
    },
    locationString() {
      // Return the region string
      if (
        this.job.company &&
        this.job.company.primary_location &&
        this.job.company.primary_location.city
      ) {
        let location = `, ${this.job.company.primary_location.city}`

        if (this.job.company.primary_location.state) {
          location += `, ${this.job.company.primary_location.state}`
        }

        return location
      }
      return ''
    },
    isCompact() {
      return this.format === 'compact'
    },
  },
}
</script>

<style scoped lang="scss">
.skills {
  .label {
    display: inline-block;
    padding-bottom: 0px;
    padding-top: 0px;
    margin-top: 0px;
  }
}
.complianceNoticeLogo {
  padding-left: 5px;
  filter: saturate(0);
  float: right;
  cursor: help;
  img {
    width: 40px;
  }
}
.complianceNoticeLogo:hover {
  filter: saturate(1);
}

.compact-list-jobs .list-group {
  h4 {
    font-size: 18px;
    line-height: 1.2;
  }
  .img-circle {
    max-width: 40px;
  }
}
</style>
